import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Flag from 'LuxKit/Flag'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import styled from 'styled-components'
import { rem } from 'polished'
import { getCountryCodeFromLocale, getNativeLanguageName } from 'i18n/util/locale'

interface Props {
  authLocale: string
  urlLocale: string
}

type ConflictChoice = 'url' | 'auth'

const LanguageWarningLabel = styled.span`
  display: inline-flex;
  gap: ${rem(8)};
  align-items: center;
`

function LocaleRedirectModal({ authLocale, urlLocale }: Props) {
  const intl = useIntl()
  const modalContext = useModalElementContext<ConflictChoice>()
  const onClose = useCallback(() => {
    modalContext.resolve('url')
  }, [modalContext])

  // The following message should always be shown in English to help with customer navigation
  const switchToAuthLanguageLabel = `Switch to ${getNativeLanguageName(authLocale)}`

  const title = intl.formatMessage({
    id: 'jVH8voIfhVh0RZcbF60E',
    defaultMessage: 'Choose your language',
    description: 'Title for a modal asking the customer to resolve a URL language and account language conflict',
  })
  const urlLanguageLabel =
    intl.formatDisplayName(urlLocale, { type: 'language' }) ?? urlLocale
  const authLanguageLabel =
    intl.formatDisplayName(authLocale, { type: 'language' }) ?? authLocale

  return (
    <Modal
      title={title}
      primaryActionText={
        <LanguageWarningLabel>
          <Flag countryCode={getCountryCodeFromLocale(authLocale)} aria-hidden />
          {switchToAuthLanguageLabel}
        </LanguageWarningLabel>
      }
      secondaryActionText={
        <LanguageWarningLabel>
          <Flag countryCode={getCountryCodeFromLocale(urlLocale)} aria-hidden />
          {intl.formatMessage({
            id: 'mvniesvnqwYRAJMKgWqJ',
            defaultMessage: 'Keep page language',
            description: 'Secondary action to keep the language currently present in the URL',
          })}
        </LanguageWarningLabel>
      }
      onPrimaryActionClick={(_event, resolve) => resolve('auth')}
      onSecondaryActionClick={(_event, resolve) => resolve('url')}
      onClose={onClose}
    >
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: '4Z4zmNyyNhN2PC0qkMZ9',
            defaultMessage:
              'This page is in {urlLanguage}, but your account language is {authLanguage}.',
            description:
              'Body text explaining a URL language and account language conflict',
          },
          {
            urlLanguage: urlLanguageLabel,
            authLanguage: authLanguageLabel,
          },
        )}
      </BodyText>
    </Modal>
  )
}

export default LocaleRedirectModal
